import React, { useMemo, useCallback, useRef, useState, useEffect } from "react";
import "./SettingsScreen.styles.css";
import {
  Box,
  Typography,
  Button,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  LinearProgress,
  Chip,
  Divider,
  Collapse,
  Tooltip,
  Slider,
  Skeleton,
  Drawer,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopyRounded";
import GroupAddIcon from "@mui/icons-material/GroupAddRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import CorporateFareRoundedIcon from "@mui/icons-material/CorporateFareRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import FolderSharedRoundedIcon from "@mui/icons-material/FolderSharedRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { WarningIcon } from "../../shared/icons";
import { useTheme as useMuiTheme, alpha } from "@mui/material/styles";
import useSettingsScreen, { PLAN_CONFIGS, BOOSTER_PACKS, normalizePlanKey } from "./useSettingsScreen";
import SEO from "../../shared/components/SEO";
import { useNavigate, useParams } from "react-router-dom";
import { useOrgAccess } from "../../shared/contexts";
import { useAuth, useUser } from "../../shared/contexts/UserContext";
import { brandConfig } from "../../shared/design/theme";
import { isConsoleHost } from "../../shared/utils";
import OrgAdminSection from "./OrganizationSection";
import HomeSection from "./HomeSection";
import KnowledgeSection from "./KnowledgeSection";
import ConnectorsSection from "./ConnectorsSection";
import { getKnowledgeAvailability, isKnowledgeLockerConfigured } from "../../shared/services/knowledgeLockerService";
import { tagChipSx, mutedChipSx, statusChipSx } from "../admin/consoleChips";
import {
  CAPABILITY_STATUSES,
  CapabilityUnavailablePanel,
  deriveConnectorCapability,
  deriveKnowledgeCapability,
  isCapabilityCatalogVisible,
} from "./capabilityStatus";

const CAPABILITY_CATALOG_VISIBLE =
  isCapabilityCatalogVisible(process.env.REACT_APP_SHOW_UNRELEASED_CAPABILITIES);
const ADMIN_SETTINGS_SECTIONS = ['members', 'credits', 'billing', 'firmstyle', 'activity'];
const BASE_SETTINGS_SECTIONS = [
  'home',
  'members',
  'credits',
  'billing',
  'firmstyle',
  'activity',
  'workspace',
  'plan',
  'account',
];

export function canShowConnectorSettings(enabled) {
  return enabled === true;
}

export function canShowKnowledgeSettings({
  catalogVisible = false,
  hasOrganization = false,
  isOrgAdmin = false,
  capabilityStatus = null,
} = {}) {
  if (catalogVisible) return true;
  return hasOrganization === true &&
    isOrgAdmin === true &&
    capabilityStatus === CAPABILITY_STATUSES.AVAILABLE;
}

export function canMountConnectorControlPlane(orgId) {
  return typeof orgId === 'string' && orgId.trim().length > 0;
}

export function shouldRedirectUnavailableConnectorSettings({
  isOrgLoading = false,
  connectorSettingsVisible = false,
  sectionParam = null,
  legacyConnectorRoute = false,
} = {}) {
  if (isOrgLoading) return false;
  return connectorSettingsVisible !== true &&
    (sectionParam === 'connectors' || legacyConnectorRoute === true);
}

export function resolveSettingsSection(sectionParam, {
  isAdmin,
  orgId,
  showWorkspace,
  connectorSettingsVisible,
  knowledgeSettingsVisible,
} = {}) {
  const validSections = [...BASE_SETTINGS_SECTIONS];
  if (connectorSettingsVisible) validSections.push('connectors');
  if (knowledgeSettingsVisible) validSections.push('knowledge');
  let activeSection = validSections.includes(sectionParam) ? sectionParam : 'home';
  if (ADMIN_SETTINGS_SECTIONS.includes(activeSection) && !isAdmin) activeSection = 'home';
  if (activeSection === 'connectors' && !connectorSettingsVisible) activeSection = 'home';
  if (activeSection === 'knowledge' && !knowledgeSettingsVisible) activeSection = 'home';
  if (activeSection === 'plan' && orgId) activeSection = 'home';
  if (activeSection === 'workspace' && !showWorkspace) activeSection = 'home';
  return activeSection;
}

// =============================================================================
// SETTINGS CONSOLE SIDEBAR
// =============================================================================

// Console-style left rail (the Anthropic/OpenAI console pattern): full-height
// surface with brand, a "back to site" link, role-shaped sections (Organization
// expands into admin sub-sections), and an identity footer with sign-out.
// On mobile it becomes a compact header with a full navigation drawer.
export const SettingsSidebar = ({ items, activeSection, onNavigate, identity, onIdentityClick, onSignOut, muiTheme, isDarkMode, onToggleDarkMode }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  // Each top-level row carries an icon and belongs to an intent-based group.
  const NAV_ICONS = {
    home: HomeRoundedIcon,
    members: GroupRoundedIcon,
    credits: BoltRoundedIcon,
    billing: ReceiptLongRoundedIcon,
    firmstyle: AutoAwesomeRoundedIcon,
    knowledge: FolderSharedRoundedIcon,
    connectors: HubRoundedIcon,
    activity: HistoryRoundedIcon,
    workspace: CorporateFareRoundedIcon,
    plan: ReceiptLongRoundedIcon,
  };

  const navButtonSx = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    width: '100%',
    textAlign: 'left',
    border: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    px: 1.5,
    py: 1,
    minHeight: 40,
    borderRadius: '8px',
    fontSize: 14,
    // Explicit Inter (the theme face) — a bare <button> otherwise falls to the
    // UA font, which is why the rail looked off against the Inter content pane.
    fontFamily: muiTheme.typography.fontFamily,
    fontWeight: active ? 600 : 500,
    color: active ? 'text.primary' : 'text.secondary',
    backgroundColor: active ? alpha(muiTheme.palette.text.primary, 0.06) : 'transparent',
    transition: 'background-color 0.2s ease, color 0.2s ease',
    '&:hover': { backgroundColor: alpha(muiTheme.palette.text.primary, 0.04), color: 'text.primary' },
    '& svg': { fontSize: 18, color: active ? 'inherit' : alpha(muiTheme.palette.text.primary, 0.55) },
  });

  // Footer utility rows (theme toggle, support, back, sign out) share one
  // quiet style with the nav rows so the whole rail reads as one system.
  const footerRowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    width: '100%',
    px: 1.5,
    py: 0.85,
    minHeight: 40,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    textAlign: 'left',
    backgroundColor: 'transparent',
    fontFamily: muiTheme.typography.fontFamily,
    fontSize: 13.5,
    fontWeight: 500,
    color: 'text.secondary',
    textDecoration: 'none',
    transition: 'background-color 0.2s ease, color 0.2s ease',
    '&:hover': { color: 'text.primary', backgroundColor: alpha(muiTheme.palette.text.primary, 0.04) },
    '& svg': { fontSize: 18, color: alpha(muiTheme.palette.text.primary, 0.55) },
  };

  const identityCaption = (() => {
    if (!identity) return '';
    const shownName = (identity.name || identity.email || '').trim().toLowerCase();
    const org = (identity.orgLabel || '').trim();
    const orgIsRedundant = !org || (shownName &&
      (org.toLowerCase().includes(shownName) || shownName.includes(org.toLowerCase())));
    return [identity.roleLabel, orgIsRedundant ? null : org].filter(Boolean).join(' · ');
  })();

  const renderNavItems = (closeAfterNavigate = false) => {
    let previousGroup = null;
    return items.map((item, index) => {
      const Icon = NAV_ICONS[item.key];
      const showGroup = item.group && item.group !== previousGroup;
      previousGroup = item.group;
      return (
        <React.Fragment key={item.key}>
          {showGroup && (
            <Typography
              component="div"
              sx={{ px: 1.5, pt: index === 0 ? 0 : 2, pb: 0.65, color: 'text.disabled', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}
            >
              {item.group}
            </Typography>
          )}
          <Box
            component="button"
            type="button"
            aria-current={activeSection === item.key ? 'page' : undefined}
            onClick={() => {
              onNavigate(item.key);
              if (closeAfterNavigate) setMobileOpen(false);
            }}
            sx={navButtonSx(activeSection === item.key)}
          >
            {Icon && <Icon />}
            <Box component="span" sx={{ flex: 1, minWidth: 0 }}>{item.label}</Box>
          </Box>
        </React.Fragment>
      );
    });
  };

  const renderIdentity = (closeAfterNavigate = false) => identity && (
    <Box
      component="button"
      type="button"
      onClick={() => {
        onIdentityClick();
        if (closeAfterNavigate) setMobileOpen(false);
      }}
      aria-label="Account settings"
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.25, width: '100%', mt: 0.25,
        px: 1.5, py: 0.85, minHeight: 44, border: 'none', borderRadius: '8px',
        cursor: 'pointer', textAlign: 'left', fontFamily: muiTheme.typography.fontFamily,
        color: 'text.primary', backgroundColor: activeSection === 'account'
          ? alpha(muiTheme.palette.text.primary, 0.06)
          : 'transparent',
        transition: 'background-color 0.2s ease',
        '&:hover': { backgroundColor: alpha(muiTheme.palette.text.primary, 0.04) },
      }}
    >
      <Avatar src={identity.photoURL || undefined} sx={{ width: 24, height: 24, fontSize: 11 }}>
        {(identity.name || identity.email || '?').charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 600, lineHeight: 1.2, color: 'text.primary' }}>
          {identity.name || identity.email}
        </Typography>
        {identityCaption && (
          <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block' }}>
            {identityCaption}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const activeLabel = items.find((item) => item.key === activeSection)?.label
    || (activeSection === 'account' ? 'Account' : 'Console');

  return (
    <>
      <Box
        component="header"
        sx={{
          display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.25,
          position: 'sticky', top: 0, zIndex: muiTheme.zIndex.appBar,
          minHeight: 58, px: 2, borderBottom: `1px solid ${muiTheme.palette.divider}`,
          backgroundColor: alpha(muiTheme.palette.background.paper, 0.96),
          backdropFilter: 'blur(12px)',
        }}
      >
        <brandConfig.icon sx={{ fontSize: 18, color: 'text.primary' }} />
        <Typography sx={{ fontWeight: 600, fontSize: 14.5, color: 'text.primary' }}>Restack</Typography>
        <Divider orientation="vertical" flexItem sx={{ my: 1.75 }} />
        <Typography noWrap sx={{ minWidth: 0, flex: 1, fontSize: 13.5, color: 'text.secondary' }}>
          {activeLabel}
        </Typography>
        <IconButton aria-label="Open console navigation" onClick={() => setMobileOpen(true)} size="small">
          <MenuRoundedIcon />
        </IconButton>
      </Box>

      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: { width: 288, maxWidth: '88vw', overflowY: 'auto' } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100dvh', px: 2, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, mb: 2 }}>
            <brandConfig.icon sx={{ fontSize: 18, color: 'text.primary' }} />
            <Typography sx={{ fontWeight: 600, fontSize: 15, color: 'text.primary', flex: 1 }}>Restack Console</Typography>
            <IconButton aria-label="Close console navigation" onClick={() => setMobileOpen(false)} size="small">
              <CloseRoundedIcon />
            </IconButton>
          </Box>
          <Box component="nav" aria-label="Settings sections" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {renderNavItems(true)}
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Divider sx={{ my: 1 }} />
          <Box component="button" type="button" onClick={onToggleDarkMode} sx={footerRowSx}>
            {isDarkMode ? <LightModeRoundedIcon /> : <DarkModeRoundedIcon />}
            {isDarkMode ? 'Light mode' : 'Dark mode'}
          </Box>
          <Box component="button" type="button" onClick={onSignOut} sx={footerRowSx}>
            <LogoutRoundedIcon />
            Sign out
          </Box>
          {renderIdentity(true)}
        </Box>
      </Drawer>

      <Box
        component="nav"
        aria-label="Settings sections"
        sx={{
          width: 248, flexShrink: 0, display: { xs: 'none', md: 'flex' },
          flexDirection: 'column', gap: 0.25, position: 'sticky', top: 0,
          height: '100dvh', overflowY: 'auto', px: 2, py: 2.5,
          borderRight: `1px solid ${muiTheme.palette.divider}`,
          backgroundColor: alpha(muiTheme.palette.text.primary, muiTheme.palette.mode === 'dark' ? 0.04 : 0.02),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, mb: 3 }}>
          <brandConfig.icon sx={{ fontSize: 18, color: 'text.primary' }} />
          <Typography sx={{ fontWeight: 600, fontSize: 15, color: 'text.primary', letterSpacing: '-0.015em', lineHeight: 1 }}>
            Restack
          </Typography>
          <Typography sx={{ fontWeight: 500, fontSize: 12, color: 'text.secondary', mt: '1px' }}>
            Console
          </Typography>
        </Box>
        {renderNavItems()}
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: 0.5 }}>
          <Box component="button" type="button" onClick={onToggleDarkMode} sx={footerRowSx}>
            {isDarkMode ? <LightModeRoundedIcon /> : <DarkModeRoundedIcon />}
            {isDarkMode ? 'Light mode' : 'Dark mode'}
          </Box>
          <Box component="button" type="button" onClick={onSignOut} sx={footerRowSx}>
            <LogoutRoundedIcon />
            Sign out
          </Box>
        </Box>
        {renderIdentity()}
      </Box>
    </>
  );
};

// =============================================================================
// SETTINGS SCREEN COMPONENT (Pure JSX)
// =============================================================================

const SettingsScreen = () => {
  const muiTheme = useMuiTheme();
  const navigate = useNavigate();
  const orgAccess = useOrgAccess();
  const { signOut } = useAuth();
  const { setSurfaceOverride, isDarkMode, toggleDarkMode } = useUser();
  const { section: sectionParam, sub: subParam } = useParams();

  // The console is its own theme surface (rustic palette + dark-mode
  // support). The provider detects it on full page loads; this covers
  // client-side navigation into /settings.
  useEffect(() => {
    setSurfaceOverride('console');
    return () => setSurfaceOverride(null);
  }, [setSurfaceOverride]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (e) { /* stay on page if sign-out fails */ }
    // Land on the marketing site; on the console subdomain "/" forwards back
    // into the console, so go absolute.
    window.location.assign(isConsoleHost() ? 'https://re-stack.com' : '/');
  };

  // Get all state and handlers from the hook
  const {
    // Auth & Navigation
    currentUser,
    isAuthenticated,
    handleBack,

    // Subscription Data
    subscriptionDetails,

    // Usage Data
    usageSummary,

    // UI State
    isProcessingPayment,
    buyingBoosterPack,
    isPodLoading,
    isCancellingSubscription,

    // Dialog State
    deleteDialogOpen,
    setDeleteDialogOpen,
    confirmDeleteText,
    setConfirmDeleteText,
    isDeletingAccount,
    deleteAccountGuidance,
    setDeleteAccountGuidance,
    stripeRedirect,

    // Notification State
    notification,
    handleCloseNotification,

    // Handlers
    handleDeleteAccount,
    handleDeleteOrganization,
    deleteOrgDialogOpen,
    setDeleteOrgDialogOpen,
    deleteOrgConfirmOpen,
    setDeleteOrgConfirmOpen,
    confirmDeleteOrgText,
    setConfirmDeleteOrgText,
    isDeletingOrg,
    deleteOrgError,
    setDeleteOrgError,
    handleSubscribe,
    handleBuyBoosterPack,
    handleManageSubscription,
    handleCancelSubscriptionNow,
    handleInviteMember,
    handleRemoveMember,
    handleCancelPendingInvite,
    cancellingInviteFor,
    seatChangePreview,
    loadingSeatChangePreview,
    confirmingSeatChange,
    requestSeatChange,
    cancelSeatChange,
    performSeatChange,
    handleCopyInviteLink,
    handleJoinPodByCode,

    // Pod Data
    pod,
    podRole,
    hasPod,
    podMembers,
    isLoadingMembers,
    inviteEmail,
    setInviteEmail,
    inviteCodeInput,
    setInviteCodeInput,
    pendingInvites,
    pendingInvitesLoading,
    acceptingInviteFor,
    decliningInviteFor,
    handleAcceptPendingInvite,
    handleDeclinePendingInvite,

    // Org seat join (license layer)
    orgJoinOptions,
    joiningOrgId,
    orgJoinNeedsVerification,
    resendingVerification,
    handleJoinOrg,
    handleResendVerification,

    // Coupon redemption
    couponCode,
    setCouponCode,
    couponLoading,
    couponResult,
    handleRedeemCoupon,

    // Seat model
    currentSeats,
    PRICE_PER_SEAT,
    CREDITS_PER_SEAT,
    MIN_SEATS,
    MAX_SEATS,
  } = useSettingsScreen();

  // Readiness controls Knowledge actions, while the catalog visibility flag
  // controls only whether built capabilities remain discoverable.
  const knowledgeLockerConfigured = isKnowledgeLockerConfigured();
  const [knowledgeAvailability, setKnowledgeAvailability] = useState(null);
  const [knowledgeAvailabilityError, setKnowledgeAvailabilityError] = useState(null);
  useEffect(() => {
    if (
      !knowledgeLockerConfigured ||
      !orgAccess.orgId ||
      !orgAccess.isOrgAdmin ||
      !currentUser
    ) {
      setKnowledgeAvailability(null);
      setKnowledgeAvailabilityError(null);
      return undefined;
    }
    const controller = new AbortController();
    let active = true;
    setKnowledgeAvailability(null);
    setKnowledgeAvailabilityError(null);
    getKnowledgeAvailability(currentUser, { signal: controller.signal })
      .then((result) => {
        if (active) setKnowledgeAvailability(result);
      })
      .catch((error) => {
        if (active && error?.name !== 'AbortError') setKnowledgeAvailabilityError(error);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [
    currentUser,
    knowledgeLockerConfigured,
    orgAccess.isOrgAdmin,
    orgAccess.orgId,
  ]);
  const knowledgeCapability = useMemo(() => deriveKnowledgeCapability({
    configured: knowledgeLockerConfigured,
    availability: knowledgeAvailability,
    availabilityError: knowledgeAvailabilityError,
    hasOrganization: Boolean(orgAccess.orgId),
    isOrgAdmin: orgAccess.isOrgAdmin,
  }), [
    knowledgeAvailability,
    knowledgeAvailabilityError,
    knowledgeLockerConfigured,
    orgAccess.isOrgAdmin,
    orgAccess.orgId,
  ]);

  // Seat selector for NEW subscriptions (free → Pro). Defaults to 1 seat (the
  // individual case); the buyer drags up to 5. Beyond 5 routes to Practice.
  const [selectedSeats, setSelectedSeats] = useState(1);

  // Resolve the active plan config (for existing subscriptions)
  const activePlanKey = pod?.subscription?.plan || 'free';
  const activePlanConfig = PLAN_CONFIGS[activePlanKey] || PLAN_CONFIGS.free;
  const isIndividualPlan = activePlanKey === 'individual';

  // Local UI state for clutter reduction (coupon collapse, copy feedback)
  const [couponExpanded, setCouponExpanded] = useState(false);
  // The manual invite-code entry is a quiet fallback (collapsed by default) so
  // the Subscribe action leads for a fresh user. It auto-opens when a code is
  // pre-filled from a /settings?inviteCode=POD-XXXX deep link (see effect below).
  const [inviteExpanded, setInviteExpanded] = useState(false);
  const [inviteCodeCopied, setInviteCodeCopied] = useState(false);

  // Auto-fill the invite code from URL (?inviteCode=POD-XXXXXX) when landing here
  // directly from an email link. One-shot.
  const hasHydratedCodeFromUrl = useRef(false);
  useEffect(() => {
    if (hasHydratedCodeFromUrl.current) return;
    // A free starter pod is not a paid membership and must not block the user
    // from accepting a real project invite.
    if (hasPod && activePlanKey !== 'free') return;
    const params = new URLSearchParams(window.location.search);
    const code = (params.get('inviteCode') || '').trim().toUpperCase();
    if (code) {
      setInviteCodeInput(code);
      hasHydratedCodeFromUrl.current = true;
    }
  }, [hasPod, activePlanKey, setInviteCodeInput]);

  // After-copy toast dismissal
  useEffect(() => {
    if (!inviteCodeCopied) return undefined;
    const t = setTimeout(() => setInviteCodeCopied(false), 1800);
    return () => clearTimeout(t);
  }, [inviteCodeCopied]);

  const handleCopyInviteCodeInline = useCallback(async () => {
    if (!pod?.inviteCode) return;
    try {
      await navigator.clipboard.writeText(pod.inviteCode);
      setInviteCodeCopied(true);
    } catch {
      handleCopyInviteLink(); // fallback path surfaces a notification
    }
  }, [pod?.inviteCode, handleCopyInviteLink]);

  const handleCopyJoinLink = useCallback(async () => {
    if (!pod?.inviteCode) return;
    // Point at /settings (not /join — that's now a thin redirect). The
    // recipient lands on Settings with the code pre-filled in the
    // "Have an invite code?" input.
    const url = `${window.location.origin}/settings?inviteCode=${encodeURIComponent(pod.inviteCode)}`;
    try {
      await navigator.clipboard.writeText(url);
      setInviteCodeCopied(true);
    } catch {
      /* noop */
    }
  }, [pod?.inviteCode]);

  const handleSendInvite = useCallback(() => {
    if (!inviteEmail.trim()) return;
    handleInviteMember(inviteEmail.trim());
  }, [inviteEmail, handleInviteMember]);

  const numberFormatter = useMemo(() => new Intl.NumberFormat("en-US"), []);
  const currencyFormatter = useMemo(() => new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }), []);
  const usdToCredits = useCallback(
    (value) => Math.max(0, Math.round((value || 0) * (100 / 50))),
    []
  );

  // Single plan: price and credits scale with the seat count (Stripe item
  // quantity). seats x $800/mo, seats x 800 pooled credits.
  const isPaidPlan = activePlanKey === 'pro';
  const activeSeats = Math.max(isPaidPlan ? 1 : 0, Number(currentSeats) || 0);

  const creditPackList = useMemo(() => Object.values(BOOSTER_PACKS), []);
  const formattedCreditPackList = useMemo(() => creditPackList.map((pack) => {
    const creditCount = pack.creditCount ?? usdToCredits(pack.valueUSD);
    return {
      ...pack,
      creditCount,
      creditCountLabel: `${numberFormatter.format(creditCount)} credits`
    };
  }), [creditPackList, numberFormatter, usdToCredits]);
  // Monthly price = seats x $800. The card subtitle shows
  // "Pro · N seats · $N,N00/mo".
  const effectiveMonthlyPriceUSD = isPaidPlan ? activeSeats * (PRICE_PER_SEAT || 0) : 0;
  const effectivePriceLabel = currencyFormatter.format(effectiveMonthlyPriceUSD);

  // Open (unfilled) seats = purchased seats minus joined members. The invite
  // controls live inside the Team card and only appear when openSeats > 0
  // (i.e. after the admin adds a seat), so there's always a seat to fill.
  const seatLimitNum = Number(pod?.seatLimit) || (isPaidPlan ? 1 : 0);
  const openSeats = Math.max(0, seatLimitNum - podMembers.length);

  // Invite-code shape check — mirrors JoinPodScreen's `/^POD-[A-Z0-9]{4,12}$/i`
  // so a typo (or a coupon code typed into the wrong box) fails inline instead
  // of round-tripping to a server error.
  const inviteCodePattern = /^POD-[A-Z0-9]{4,12}$/i;
  const inviteCodeTrimmed = (inviteCodeInput || '').trim();
  const inviteCodeMalformed = inviteCodeTrimmed.length > 0 && !inviteCodePattern.test(inviteCodeTrimmed);

  // Section refs and navigation
  const settingsScreenRef = useRef(null);

  // ── Console sections ──
  // The sidebar is shaped by role: Organization for org members (admins get
  // the sub-sections), Team for paid pod members, Plan & billing for non-org
  // users, Account for everyone. Section visibility mirrors the SAME
  // conditions the content blocks below already use.
  // Org users manage people in Organization -> Members (one roster, one
  // invite door). The standalone Project section only remains for legacy
  // non-org paid pods.
  const showWorkspace = hasPod && activePlanKey !== 'free' && !isIndividualPlan && !orgAccess.orgId;
  // Flat sidebar (the cleanup): Dashboard for everyone; for an org ADMIN the
  // org surface is promoted to top-level Members / Credits / Billing (no
  // "Organization" wrapper). Account is reached via the identity footer, not a
  // nav row. Non-org users get Plan & billing; legacy non-org paid pods keep
  // Project.
  const isAdmin = orgAccess.canAccessAdmin;
  const connectorSettingsVisible = canShowConnectorSettings(CAPABILITY_CATALOG_VISIBLE);
  const knowledgeSettingsVisible = canShowKnowledgeSettings({
    catalogVisible: CAPABILITY_CATALOG_VISIBLE,
    hasOrganization: Boolean(orgAccess.orgId),
    isOrgAdmin: orgAccess.isOrgAdmin,
    capabilityStatus: knowledgeCapability.status,
  });
  const noOrganizationConnectorCapability = useMemo(
    () => deriveConnectorCapability(null, { hasOrganization: false }),
    []
  );
  const navItems = [
    { key: 'home', label: 'Dashboard', group: 'Overview', show: true },
    { key: 'members', label: 'Members', group: 'Organization', show: isAdmin },
    { key: 'firmstyle', label: 'Firm style', group: 'Organization', show: isAdmin },
    { key: 'knowledge', label: 'Knowledge', group: 'Organization', show: knowledgeSettingsVisible },
    { key: 'connectors', label: 'Connectors', group: 'Organization', show: connectorSettingsVisible },
    { key: 'activity', label: 'Activity', group: 'Organization', show: isAdmin },
    { key: 'credits', label: 'Credits', group: 'Usage and billing', show: isAdmin },
    { key: 'billing', label: 'Billing', group: 'Usage and billing', show: isAdmin },
    { key: 'workspace', label: 'Project', group: 'Workspace', show: showWorkspace },
    { key: 'plan', label: 'Plan and billing', group: 'Billing', show: !orgAccess.orgId },
  ].filter((i) => i.show);
  const defaultSection = 'home';
  // Titles + one-line descriptions per section (Dashboard renders its own
  // greeting header instead). Account is reachable via the footer though it
  // has no nav row.
  const SECTION_TITLES = {
    home: 'Dashboard', members: 'Members', credits: 'Credits', billing: 'Billing',
    firmstyle: 'Firm style', knowledge: 'Knowledge', connectors: 'Connectors', activity: 'Activity', workspace: 'Project', plan: 'Plan & billing', account: 'Account',
  };
  const SECTION_DESCRIPTIONS = {
    members: 'Your team, seats, and invites.',
    credits: 'The shared credit pool, top-ups, and per-member usage.',
    billing: 'Plan, seats, payment method, and invoices.',
    firmstyle: "Always-on instructions Restack applies to every deck for your firm.",
    knowledge: 'Secure firm and project documents available to your Office agents.',
    connectors: 'Use reviewed financial sources in live Office workflows.',
    activity: 'Recorded organization activity, with actor, action, and time metadata.',
    workspace: 'Your project workspace and teammates.',
    plan: 'Your subscription, credits, and billing.',
    account: 'Your profile and account controls.',
  };
  // Resolve the active section, falling back to Dashboard when the URL names a
  // section this user can't see (e.g. a non-admin hitting /settings/members,
  // or an org user hitting /settings/plan).
  const activeSection = resolveSettingsSection(sectionParam || defaultSection, {
    isAdmin,
    orgId: orgAccess.orgId,
    showWorkspace,
    connectorSettingsVisible,
    knowledgeSettingsVisible,
  });
  const goSection = (key, sub = null) =>
    navigate(`/settings/${key}${sub ? `/${sub}` : ''}`);

  // Legacy redirect: the old /settings/organization(/:sub) routes map onto the
  // flat ones (usage folded into credits). Keeps post-checkout links, invite
  // emails, and bookmarks working.
  useEffect(() => {
    if (sectionParam !== 'organization') return;
    const map = { seats: 'members', credits: 'credits', billing: 'billing', usage: 'credits' };
    const target = map[subParam] || 'members';
    const search = typeof window !== 'undefined' ? window.location.search : '';
    navigate(`/settings/${target}${search}`, { replace: true });
  }, [sectionParam, subParam, navigate]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const legacyConnectorRoute = !sectionParam && params.get('section') === 'connectors';
    if (shouldRedirectUnavailableConnectorSettings({
      isOrgLoading: orgAccess.isLoading,
      connectorSettingsVisible,
      sectionParam,
      legacyConnectorRoute,
    })) {
      navigate('/settings/home', { replace: true });
      return;
    }
    if (orgAccess.isLoading) return;
    if (!legacyConnectorRoute || !orgAccess.orgId) return;
    params.delete('section');
    const search = params.toString();
    navigate(`/settings/connectors${search ? `?${search}` : ''}`, { replace: true });
  }, [
    connectorSettingsVisible,
    sectionParam,
    navigate,
    orgAccess.isLoading,
    orgAccess.orgId,
  ]);
  const identity = currentUser
    ? {
        name: currentUser.displayName || null,
        email: currentUser.email || null,
        photoURL: currentUser.photoURL || null,
        roleLabel: orgAccess.isOrgAdmin
          ? 'Org Admin'
          : orgAccess.isBillingAdmin
            ? 'Billing Admin'
            : orgAccess.orgId
              ? 'Member'
              : podRole === 'admin'
                ? 'Project Admin'
                : null,
        // Firm context for the caption ("Org Admin · Acme Capital"). The pod
        // name doubles as the firm name for migrated orgs (orgId == podId).
        orgLabel: pod?.name || null,
      }
    : null;
  // Self-serve deletion is available; the server enforces the same rule and
  // additionally requires a recent re-auth plus the exact confirmation phrase.
  const selfServeAccountDeletionAvailable = true;
  // An admin is blocked only when leaving would actually orphan someone. The
  // previous rule blocked every admin, including a sole member with nobody to
  // hand over to, which left the only person who could delete the account
  // unable to. Ownership still has to move first when other members exist.
  const orgHasOtherMembers = Number(pod?.memberCount || 0) > 1;
  const accountDeletionRoleBlocked =
    (orgAccess.isOrgAdmin || orgAccess.isBillingAdmin || podRole === 'admin') && orgHasOtherMembers;
  const accountDeletionBlocked = !selfServeAccountDeletionAvailable || accountDeletionRoleBlocked;

  return (
    <>
      <SEO title="Settings" description="Manage your Restack subscription, project, billing, and account." noIndex />
      <Box ref={settingsScreenRef} className="settings-screen" data-theme={muiTheme.palette.mode} sx={{ minHeight: '100dvh' }}>
        {/* Console layout (the Anthropic/OpenAI pattern): full-height left
            rail with brand + nav + identity, content pane on the right with a
            page title per section. Desktop chrome lives in the rail. Mobile
            uses a compact sticky header and full navigation drawer. */}
        {isAuthenticated ? (
          <Box
            className="settings-content"
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              minHeight: '100dvh',
            }}
          >
            <SettingsSidebar
              items={navItems}
              activeSection={activeSection}
              onNavigate={goSection}
              identity={identity}
              onIdentityClick={() => goSection('account')}
              onSignOut={handleSignOut}
              muiTheme={muiTheme}
              isDarkMode={isDarkMode}
              onToggleDarkMode={toggleDarkMode}
            />
            {/* Content pane uses the full available width (no narrow cap) so
                the dashboard, seats table, and home stat row can spread out.
                On desktop it owns the scroll (full viewport height, scrolls
                internally) so the sidebar stays fixed; on mobile the page
                scrolls normally. */}
            <Box sx={{ flex: 1, minWidth: 0, width: '100%', px: { xs: 2, sm: 4, md: 6 }, py: { xs: 2.5, md: 4.5 }, height: { md: '100dvh' }, overflowY: { md: 'auto' } }}>
            {/* Page title + one-line description — the active section,
                console-style. Home renders its own greeting header instead. */}
            {activeSection !== 'home' && (
              <Box sx={{ mb: 3.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, letterSpacing: '-0.02em' }}>
                  {SECTION_TITLES[activeSection] || 'Settings'}
                </Typography>
                {SECTION_DESCRIPTIONS[activeSection] && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {SECTION_DESCRIPTIONS[activeSection]}
                  </Typography>
                )}
              </Box>
            )}
            {(isPodLoading || orgAccess.isLoading) ? (
              // Skeleton shaped like the landing sections (a title line + a
              // card row), so content landing doesn't jump the layout.
              <Box className="settings-section" sx={{ mb: 6 }}>
                <Skeleton variant="text" width={220} sx={{ fontSize: 24, mb: 1 }} />
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {[0, 1, 2].map((i) => (
                    <Box key={i} className="settings-surface-card" sx={{ flex: '1 1 200px', p: 2.5 }}>
                      <Skeleton variant="text" width={90} sx={{ fontSize: 11 }} />
                      <Skeleton variant="text" width={120} sx={{ fontSize: '1.75rem' }} />
                      <Skeleton variant="text" width={70} sx={{ fontSize: 12 }} />
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              <>
                {/* Home — the console landing: greeting, role-shaped stat
                    cards that deep-link into sections, and a resources row.
                    The reference dashboard pattern. */}
                {activeSection === 'home' && (
                  <Box className="settings-section" sx={{ mb: 6 }}>
                    <HomeSection
                      goSection={goSection}
                      usageSummary={usageSummary}
                      activePlanKey={activePlanKey}
                      activeSeats={activeSeats}
                      pod={pod}
                    />
                  </Box>
                )}

                {/* Org admin surface, flattened to top-level sections. Each
                    renders one tab; the wrapper handles the role gate + loading
                    and reads only the role-gated endpoints. CFE-07: gate on
                    canAccessAdmin (admin/billing capability), not mere org
                    membership, so the render condition is self-sufficient and a
                    non-admin member can never mount the admin section. */}
                {/* key={activeSection} remounts the wrapper when the admin
                    route changes, so the section-enter animation runs on
                    every tab switch, not just the first. */}
                {ADMIN_SETTINGS_SECTIONS.includes(activeSection) && orgAccess.canAccessAdmin && (
                  <Box key={activeSection} className="settings-section" sx={{ mb: 6 }}>
                    <OrgAdminSection section={activeSection} />
                  </Box>
                )}

                {activeSection === 'knowledge' && knowledgeSettingsVisible && (
                  <Box key={`${activeSection}-${subParam || 'sources'}`} className="settings-section" sx={{ mb: 6 }}>
                    <KnowledgeSection sub={subParam} capability={knowledgeCapability} />
                  </Box>
                )}

                {connectorSettingsVisible &&
                  activeSection === 'connectors' && (
                  <Box key={activeSection} className="settings-section" sx={{ mb: 6 }}>
                    {canMountConnectorControlPlane(orgAccess.orgId) ? (
                      <ConnectorsSection />
                    ) : (
                      <CapabilityUnavailablePanel
                        capability={noOrganizationConnectorCapability}
                        title="Connectors"
                        actionLabel="Connect"
                      />
                    )}
                  </Box>
                )}

                {/* Subscription — current plan + credit balances. Shown to
                    NON-org users only (free / trial, who are looking at their
                    own balance). For an organization, the pooled credits, seats,
                    and billing live in the Organization section, Admins only. */}
                {activeSection === 'plan' && !orgAccess.orgId && (activePlanKey !== 'free' || usageSummary.prepaidCreditsBalance > 0) && (
                <Box className="settings-section" sx={{ mb: 6 }}>
                  <Typography variant="h6" className="section-title" sx={{ mb: 3 }}>
                    {activePlanKey !== 'free' ? 'Subscription' : 'Your credits'}
                  </Typography>

                  {/* ── Card 1: subscription metadata + monthly credits. Paid
                      only — a free user's "0 monthly credits" card is noise;
                      they get the Subscribe card below instead. ── */}
                  {activePlanKey !== 'free' && (
                  <Box className="settings-surface-card" sx={{ p: { xs: 2.5, md: 3 }, mb: 2 }}>
                    {/* Header row — minimal: pod name as the primary line,
                        plan + price as a single caption, project code shown
                        only when set. Avoids the previous wall of stacked
                        labels (overline, h5, body, body) for the same data. */}
                    {hasPod && activePlanKey !== 'free' && (
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, mb: 3 }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                            {pod?.name || activePlanConfig.name}
                          </Typography>
                          {/* Headline: plan name · seats · $/month. Seats =
                              Stripe subscription item quantity; price = seats
                              x $800. */}
                          {(() => {
                            const totalSeats = activeSeats;
                            const seatsLabel = totalSeats === 1 ? 'seat' : 'seats';
                            return (
                              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
                                {activePlanConfig.name} · {totalSeats} {seatsLabel} · {effectivePriceLabel}/month
                              </Typography>
                            );
                          })()}
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", display: "block", mt: 0.25, opacity: 0.75 }}
                          >
                            ({activeSeats} seat{activeSeats === 1 ? '' : 's'} × {currencyFormatter.format(PRICE_PER_SEAT || 0)})
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={podRole === "admin" ? "Admin" : "Member"}
                          sx={podRole === "admin" ? tagChipSx(muiTheme) : mutedChipSx(muiTheme)}
                        />
                      </Box>
                    )}

                    {/* Cancellation notice. Render in UTC so the displayed
                        day matches Stripe's billing day (the cycle is anchored
                        to UTC midnight on the 1st of the month; rendering in
                        local time pulls it back to "the 31st" in any
                        UTC-negative timezone, which confused customers). */}
                    {subscriptionDetails?.cancelAtPeriodEnd && subscriptionDetails?.cycleEndsAt && (
                      <Alert severity="warning" sx={{ mb: 3, borderRadius: "10px" }}>
                        Your subscription ends on {new Date(subscriptionDetails.cycleEndsAt).toLocaleDateString(undefined, { timeZone: 'UTC' })}.
                        You'll retain access until then.
                      </Alert>
                    )}

                    {/* Monthly credits sub-block */}
                    <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 600, letterSpacing: "0.05em", display: "block", mb: 1 }}>
                      Monthly credits
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1 }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 600,
                          letterSpacing: "-0.03em",
                          lineHeight: 1,
                          color: usageSummary.subscriptionCreditsBalance <= 20 && usageSummary.subscriptionCreditsBalance > 0
                            ? "warning.main"
                            : usageSummary.subscriptionCreditsBalance === 0
                              ? "text.secondary"
                              : "text.primary",
                        }}
                      >
                        {numberFormatter.format(Math.floor(usageSummary.subscriptionCreditsBalance))}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                        {usageSummary.baseCredits > 0
                          ? `of ${numberFormatter.format(usageSummary.baseCredits)} credits`
                          : "credits"}
                      </Typography>
                    </Box>

                    {activePlanKey === 'free' && (
                      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2.5 }}>
                        {usageSummary.subscriptionCreditsBalance > 0
                          ? "Subscribe for monthly credits"
                          : "Subscribe to get started"}
                      </Typography>
                    )}

                    {/* Monthly progress bar */}
                    {(() => {
                      const total = Math.max(usageSummary.baseCredits, usageSummary.subscriptionCreditsBalance, 1);
                      const pct = Math.min(100, (usageSummary.subscriptionCreditsBalance / total) * 100);
                      const topMargin = activePlanKey !== 'free' ? 2 : 0;
                      const barColor = pct <= 20
                        ? muiTheme.palette.error.main
                        : pct <= 50
                          ? muiTheme.palette.warning.main
                          : muiTheme.palette.primary.main;
                      return (
                        <LinearProgress
                          variant="determinate"
                          value={pct}
                          sx={{
                            mt: topMargin,
                            height: 10,
                            borderRadius: 10,
                            backgroundColor: alpha(muiTheme.palette.primary.main, 0.1),
                            "& .MuiLinearProgress-bar": {
                              borderRadius: 10,
                              backgroundColor: barColor,
                              transition: "transform 0.6s ease, background-color 0.4s ease",
                            },
                          }}
                        />
                      );
                    })()}

                    {/* Renews date — Stripe-authoritative currentPeriodEnd.
                        Rendered in UTC: Stripe anchors the billing cycle to
                        UTC midnight on the 1st, so a renewal date of
                        "2026-06-01T00:00:00.000Z" is genuinely "June 1" from
                        the billing system's perspective. Without the UTC
                        timeZone option, toLocaleDateString() would render it
                        as May 31 in any UTC-negative zone (Americas) — a
                        full day off from when Stripe will actually charge. */}
                    {(subscriptionDetails?.currentPeriodEnd || subscriptionDetails?.cycleEndsAt) && (
                      <Typography variant="caption" sx={{ color: "text.secondary", mt: 2, display: "block" }}>
                        Renews {new Date(subscriptionDetails.currentPeriodEnd || subscriptionDetails.cycleEndsAt).toLocaleDateString(undefined, { timeZone: 'UTC' })}
                      </Typography>
                    )}

                    {/* Admin actions — manage subscription / upgrade. Moved
                        here from the now-deleted standalone Subscription
                        section so admin can do it in-context. */}
                    {hasPod && activePlanKey !== 'free' && podRole === "admin" && (
                      <>
                        <Divider sx={{ my: 2.5 }} />
                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleManageSubscription}
                            disabled={stripeRedirect}
                            sx={{ borderRadius: "8px", textTransform: "none" }}
                          >
                            Manage subscription
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={handleCancelSubscriptionNow}
                            disabled={isCancellingSubscription || subscriptionDetails?.cancelAtPeriodEnd}
                            sx={{ borderRadius: "8px", textTransform: "none" }}
                          >
                            {isCancellingSubscription ? "Cancelling..." : "Cancel now"}
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                  )}

                  {/* ── Card 2: Add-on credits — visible to ANY pod member
                         on a paid plan (the team's add-on balance is shared,
                         so everyone should see the same number). Members
                         see the balance read-only; only admins see the
                         "Top up" purchase buttons further down. Free-tier
                         users still see this card if they have a non-zero
                         balance from a SANDBOX redemption. */}
                  {(activePlanKey !== 'free' || usageSummary.prepaidCreditsBalance > 0) && (
                    <Box className="settings-surface-card" sx={{ p: { xs: 2.5, md: 3 } }}>
                      {/* Header with info-icon tooltip — replaces the
                          previous on-the-nose paragraph note. The tooltip
                          surfaces the don't-expire detail only when the
                          user actively asks for it. */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 600, letterSpacing: "0.05em" }}>
                          Add-on credits
                        </Typography>
                        <Tooltip
                          title="Monthly credits roll over up to 2x your monthly allotment while your subscription is active. Add-on credits last for a full year and kick in once your monthly credits run out."
                          arrow
                          placement="top"
                        >
                          <IconButton size="small" sx={{ p: 0.25, color: 'text.secondary' }} aria-label="About add-on credits">
                            <InfoRoundedIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1 }}>
                        <Typography
                          variant="h3"
                          sx={{
                            fontWeight: 600,
                            letterSpacing: "-0.03em",
                            lineHeight: 1,
                            color: usageSummary.prepaidCreditsBalance > 0 ? "text.primary" : "text.secondary",
                          }}
                        >
                          {numberFormatter.format(Math.floor(usageSummary.prepaidCreditsBalance))}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                          credits in reserve
                        </Typography>
                      </Box>

                      {/* Add-on bar — green/success to read as "stockpile" */}
                      {(() => {
                        const reference = Math.max(usageSummary.baseCredits, usageSummary.prepaidCreditsBalance, 1);
                        const pct = Math.min(100, (usageSummary.prepaidCreditsBalance / reference) * 100);
                        const barColor = usageSummary.prepaidCreditsBalance > 0
                          ? muiTheme.palette.success.main
                          : muiTheme.palette.action.disabled;
                        return (
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            sx={{
                              mt: 2,
                              height: 10,
                              borderRadius: 10,
                              backgroundColor: alpha(muiTheme.palette.success.main, 0.1),
                              "& .MuiLinearProgress-bar": {
                                borderRadius: 10,
                                backgroundColor: barColor,
                                transition: "transform 0.6s ease, background-color 0.4s ease",
                              },
                            }}
                          />
                        );
                      })()}

                      {/* Top-up packs — admin only on paid plans */}
                      {podRole === "admin" && activePlanKey !== 'free' && formattedCreditPackList.length > 0 && (
                        <>
                          <Divider sx={{ my: 2.5 }} />
                          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5 }}>
                            Top up
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {formattedCreditPackList.map((pack) => (
                              <Box
                                key={pack.key}
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: 1.5,
                                  py: 0.5
                                }}
                              >
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                  {pack.creditCountLabel} for {currencyFormatter.format(pack.retailPriceUSD)}
                                </Typography>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleBuyBoosterPack(pack.key)}
                                  disabled={!!buyingBoosterPack}
                                  sx={{ borderRadius: "8px", textTransform: "none", minWidth: 100 }}
                                >
                                  {buyingBoosterPack === pack.key ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    "Buy credits"
                                  )}
                                </Button>
                              </Box>
                            ))}
                          </Box>
                        </>
                      )}
                    </Box>
                  )}
                </Box>
                )}

                {/* ── Org seat invites + domain claim — the LICENSE layer.
                    An org admin reserved a seat for this email, or the user's
                    verified work-email domain matches their firm's org. Both
                    accept paths require a verified email (server-enforced);
                    the unverified state surfaces a resend button instead of a
                    dead-end 403. ── */}
                {(activeSection === 'home' || activeSection === 'plan') && !orgAccess.orgId && (orgJoinOptions.invites.length > 0 || orgJoinOptions.domainOrg) && (
                  <Box className="settings-section" sx={{ mb: 6 }}>
                    <Typography variant="h6" className="section-title" sx={{ mb: 3 }}>
                      Your firm has Restack
                    </Typography>
                    {[...orgJoinOptions.invites.map((o) => ({ ...o, viaDomain: false })),
                      ...(orgJoinOptions.domainOrg ? [{ ...orgJoinOptions.domainOrg, viaDomain: true }] : [])
                    ].map((o) => (
                      <Box
                        key={o.orgId}
                        className="settings-surface-card"
                        sx={{
                          p: { xs: 2.5, md: 3 },
                          mb: 2,
                          border: `1px solid ${muiTheme.palette.divider}`,
                          backgroundColor: alpha(muiTheme.palette.text.primary, 0.025),
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.25 }}>
                              {o.viaDomain
                                ? <>Your work email matches <strong>{o.orgName}</strong></>
                                : <>You have a seat waiting at <strong>{o.orgName}</strong></>}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {o.viaDomain
                                ? 'Claim a seat on your firm\'s plan and start working in PowerPoint.'
                                : 'An admin reserved a seat for you on your firm\'s plan.'}
                            </Typography>
                          </Box>
                          <Box sx={{ flexShrink: 0 }}>
                            <Button
                              variant="contained"
                              onClick={() => handleJoinOrg(o.orgId, { viaDomain: o.viaDomain })}
                              disabled={joiningOrgId === o.orgId}
                              sx={{ borderRadius: '8px', textTransform: 'none', px: 2.5 }}
                            >
                              {joiningOrgId === o.orgId ? 'Claiming…' : 'Claim your seat'}
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                    {(orgJoinNeedsVerification || !orgJoinOptions.emailVerified) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'text.secondary', fontSize: 14 }}>
                        <span>Seats require a verified email address.</span>
                        <Button
                          size="small"
                          variant="text"
                          onClick={handleResendVerification}
                          disabled={resendingVerification}
                          sx={{ textTransform: 'none' }}
                        >
                          {resendingVerification ? 'Sending…' : 'Resend verification email'}
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}

                {/* ── Auto-detected pending invites — surfaces ANY pod where
                    the admin invited this user's email. PRIMARY discovery path
                    (no email required, no code typing required). Pods only
                    appear here if the user's signed-in email is on the pod's
                    pendingInvites list. Click Accept → joins. Click Decline
                    → admin's invite is removed; admin can re-invite later. ── */}
                {(activeSection === 'home' || activeSection === 'plan') && (!hasPod || activePlanKey === 'free') && pendingInvites && pendingInvites.length > 0 && (
                  <Box className="settings-section" sx={{ mb: 6 }}>
                    <Typography variant="h6" className="section-title" sx={{ mb: 3 }}>
                      You've been invited
                    </Typography>
                    {pendingInvites.map((inv) => (
                      <Box
                        key={inv.podId}
                        className="settings-surface-card"
                        sx={{
                          p: { xs: 2.5, md: 3 },
                          mb: 2,
                          border: `1px solid ${muiTheme.palette.divider}`,
                          backgroundColor: alpha(muiTheme.palette.text.primary, 0.025),
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.25 }}>
                              {inv.inviterName || inv.inviterEmail || 'A teammate'} invited you to <strong>{inv.podName}</strong>
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {normalizePlanKey(inv.plan) === 'pro' ? 'Pro plan' : 'Restack project'}
                              {' · '}
                              {Math.max(0, Number(inv.seatLimit || 0) - Number(inv.memberCount || 0))} seat{Math.max(0, Number(inv.seatLimit || 0) - Number(inv.memberCount || 0)) === 1 ? '' : 's'} available
                              {inv.inviterEmail ? ` · from ${inv.inviterEmail}` : ''}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                            <Button
                              variant="contained"
                              onClick={() => handleAcceptPendingInvite(inv.podId)}
                              disabled={acceptingInviteFor === inv.podId || decliningInviteFor === inv.podId}
                              sx={{ borderRadius: '8px', textTransform: 'none', px: 2.5 }}
                            >
                              {acceptingInviteFor === inv.podId ? 'Joining…' : 'Accept'}
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() => handleDeclinePendingInvite(inv.podId)}
                              disabled={acceptingInviteFor === inv.podId || decliningInviteFor === inv.podId}
                              sx={{ borderRadius: '8px', textTransform: 'none', px: 2.5 }}
                            >
                              {decliningInviteFor === inv.podId ? 'Declining…' : 'Decline'}
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Loading hint while we check for pending invites — only
                    shown briefly on first load for pod-less users. */}
                {(activeSection === 'home' || activeSection === 'plan') && (!hasPod || activePlanKey === 'free') && pendingInvitesLoading && (!pendingInvites || pendingInvites.length === 0) && (
                  <Box sx={{ mb: 4, color: 'text.secondary', fontSize: 14, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={14} />
                    <span>Checking for invites…</span>
                  </Box>
                )}

                {/* ── Manual code input — fallback for cases where the auto-
                    detect path doesn't match (e.g. admin invited a different
                    email than the user signed up with). Visible to anyone
                    who isn't on a paid plan yet (free-tier users included,
                    since they're effectively "no real pod" — onUserCreated
                    auto-creates a free pod for every new signup). ── */}
                {(activeSection === 'home' || activeSection === 'plan') && (!hasPod || activePlanKey === 'free') && (
                  <Box className="settings-section" sx={{ mb: 6 }}>
                    <Button
                      variant="text"
                      onClick={() => setInviteExpanded((v) => !v)}
                      startIcon={<GroupAddIcon sx={{ fontSize: 18 }} />}
                      endIcon={<ExpandMoreIcon sx={{ transform: (inviteExpanded || !!inviteCodeTrimmed) ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }} />}
                      sx={{ textTransform: "none", color: "text.secondary", fontWeight: 500, fontSize: "0.9rem", px: 0 }}
                    >
                      Have an invite code? Join your team
                    </Button>
                    <Collapse in={inviteExpanded || !!inviteCodeTrimmed} timeout="auto" unmountOnExit>
                      <Box
                        className="settings-surface-card"
                        sx={{
                          mt: 1.5,
                          p: { xs: 2.5, md: 3 },
                          border: `1px solid ${muiTheme.palette.divider}`,
                          backgroundColor: alpha(muiTheme.palette.text.primary, 0.02),
                        }}
                      >
                        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                          Paste the <strong>invite code</strong> your admin shared (email, Slack, however
                          they sent it). You'll join their project and share the credit pool.
                          The invite is tied to your email; make sure you signed up with
                          the same address your admin invited.
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", alignItems: "flex-start" }}>
                          <TextField
                            size="small"
                            label="Invite code"
                            placeholder="POD-XXXXXX"
                            value={inviteCodeInput}
                            onChange={(event) => setInviteCodeInput(event.target.value.toUpperCase())}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                if (!inviteCodeMalformed) handleJoinPodByCode();
                              }
                            }}
                            error={inviteCodeMalformed}
                            helperText={inviteCodeMalformed ? "Codes look like POD-XXXXXX" : " "}
                            inputProps={{ style: { letterSpacing: "0.08em", fontFamily: "inherit" } }}
                            sx={{ flex: "1 1 220px", maxWidth: 280 }}
                          />
                          <Button
                            variant="contained"
                            onClick={handleJoinPodByCode}
                            disabled={!inviteCodeTrimmed || inviteCodeMalformed}
                            sx={{ borderRadius: "8px", textTransform: "none", px: 3, mt: 0.25 }}
                          >
                            Join team
                          </Button>
                        </Box>
                      </Box>
                    </Collapse>
                  </Box>
                )}

                {/* ── Plans / Subscribe — only when no pod, or still on free ── */}
                {activeSection === 'plan' && (!hasPod || activePlanKey === 'free') && (
                  <Box className="settings-section" sx={{ mb: 6 }}>
                    <Typography variant="h6" className="section-title" sx={{ mb: 1 }}>
                      Start your subscription
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
                      One plan, $800 per seat per month. Pick your seats; you'll become the admin and get a code to invite teammates.
                    </Typography>

                    {/* Pro plan — single per-seat plan with a 1 to 5 seat selector (6+ is Practice) */}
                    <Box
                      className="settings-surface-card"
                      sx={{
                        p: { xs: 2.5, md: 3 },
                        mb: 2,
                        border: `1px solid ${muiTheme.palette.divider}`,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Pro · {currencyFormatter.format(selectedSeats * (PRICE_PER_SEAT || 0))}/mo
                        </Typography>
                        <Chip size="small" variant="outlined" label="Per seat" sx={tagChipSx(muiTheme)} />
                      </Box>
                      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                        {numberFormatter.format(selectedSeats * (CREDITS_PER_SEAT || 0))} credits / month{selectedSeats > 1 ? ', pooled' : ''} · {selectedSeats} seat{selectedSeats === 1 ? '' : 's'} · billed monthly · project code on receipt
                      </Typography>

                      {/* Seat selector — price and credits scale linearly, 1 to 5 */}
                      <Box sx={{ px: 0.5, mb: 1 }}>
                        <Slider
                          value={selectedSeats}
                          onChange={(_, v) => setSelectedSeats(Array.isArray(v) ? v[0] : v)}
                          min={MIN_SEATS || 1}
                          max={MAX_SEATS || 5}
                          step={1}
                          marks
                          valueLabelDisplay="auto"
                          aria-label="Number of seats"
                          disabled={isProcessingPayment}
                        />
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>1 seat</Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>{MAX_SEATS || 5} seats</Typography>
                        </Box>
                      </Box>

                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => handleSubscribe(selectedSeats)}
                        disabled={isProcessingPayment}
                        sx={{ borderRadius: "8px", textTransform: "none", px: 3, mt: 1 }}
                      >
                        Subscribe · {currencyFormatter.format(selectedSeats * (PRICE_PER_SEAT || 0))}/mo
                      </Button>

                      {/* Self-serve tops out at 5 seats; 6+ is the sales-assisted
                          Practice tier. Surface the conversation at the cap. */}
                      {selectedSeats >= (MAX_SEATS || 5) && (
                        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '8px', border: `1px solid ${alpha(muiTheme.palette.primary.main, 0.3)}`, backgroundColor: alpha(muiTheme.palette.primary.main, 0.05) }}>
                          <Typography variant="body2" sx={{ color: 'text.primary' }}>
                            Need 6+ seats?{' '}
                            <Box component="a" href="mailto:hi@re-stack.com?subject=Restack%20Practice%20pricing%20for%20our%20team" sx={{ color: 'primary.main', fontWeight: 600 }}>
                              Talk to us
                            </Box>
                            {' '}about Practice — volume pricing, pooled credits, annual / invoicing.
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Practice — custom / sales-led, no self-serve checkout */}
                    <Box className="settings-surface-card" sx={{ p: { xs: 2.5, md: 3 } }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1.5 }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Practice · Custom
                          </Typography>
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            More than 5 seats, a negotiated rate, annual terms, or invoicing (net-30). Roll out across your firm.
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          href="mailto:hi@re-stack.com?subject=Restack%20Practice%20Plan%20Inquiry"
                          sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}
                        >
                          Contact us
                        </Button>
                      </Box>
                    </Box>

                    {/* ── Coupon (collapsible, tucked under plans) ── */}
                    <Box sx={{ mt: 2.5 }}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => setCouponExpanded((prev) => !prev)}
                        endIcon={
                          <ExpandMoreIcon
                            sx={{
                              transform: couponExpanded ? "rotate(180deg)" : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                            }}
                          />
                        }
                        startIcon={<LocalOfferRoundedIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          textTransform: "none",
                          color: "text.secondary",
                          fontWeight: 500,
                          fontSize: "0.85rem",
                          px: 0,
                        }}
                      >
                        Have a promo or trial code?
                      </Button>
                      <Collapse in={couponExpanded} timeout="auto" unmountOnExit>
                        <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mt: 1, mb: 1 }}>
                          A discount or trial code from a Restack email (e.g. SANDBOX). This is different from a
                          team <strong>invite code</strong> (POD-XXXXXX); use “Join your team” above for that.
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                          <TextField
                            size="small"
                            placeholder="Trial code (e.g. SANDBOX)"
                            value={couponCode}
                            onChange={(event) => setCouponCode(event.target.value.toUpperCase())}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                handleRedeemCoupon();
                              }
                            }}
                            disabled={couponLoading}
                            sx={{ flex: "1 1 200px", maxWidth: 240 }}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleRedeemCoupon}
                            disabled={couponLoading || !couponCode.trim()}
                            sx={{ textTransform: "none", fontWeight: 600, px: 2 }}
                          >
                            {couponLoading ? <CircularProgress size={16} color="inherit" /> : "Redeem"}
                          </Button>
                        </Box>
                        {couponResult && (
                          <Typography
                            sx={{
                              mt: 1,
                              fontSize: "0.8rem",
                              color: couponResult.success ? muiTheme.palette.success.main : muiTheme.palette.error.main,
                            }}
                          >
                            {couponResult.message}
                          </Typography>
                        )}
                      </Collapse>
                    </Box>
                  </Box>
                )}

                {/* Subscription details (plan name, project code, admin
                    actions) used to live in their own section below — they
                    are now merged into the Subscription card above so users
                    see plan + monthly credits in one place. */}
                {activeSection === 'workspace' && hasPod && activePlanKey !== 'free' && (
                  <>

                    {!isIndividualPlan && (
                    <Box className="settings-section" sx={{ mb: 6 }}>
                      <Typography variant="h6" className="section-title" sx={{ mb: 3 }}>
                        Team
                      </Typography>

                      {/* ── Members roster + pending tracker ──
                          Shows joined members AND pending invites in one
                          list. Admin can cancel pending invites; pod doc
                          onSnapshot keeps this live (no manual refresh). */}
                      <Box className="settings-surface-card" sx={{ p: { xs: 2.5, md: 3 } }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Project · {podMembers.length}{Array.isArray(pod?.pendingInvites) && pod.pendingInvites.length > 0 ? ` + ${pod.pendingInvites.length} pending` : ''} / {pod?.seatLimit || 1} seats
                          </Typography>
                          {/* Seat controls — pod admins only. Seats are the
                              Stripe subscription item quantity (1 to 5). Adds
                              bill immediately (a prorated charge for the rest of
                              the cycle). Removes do not receive a mid-cycle
                              refund. "− Seat" is blocked below the active member
                              and pending invitation count. Beyond 5, the buyer is routed to Practice
                              (contact us). */}
                          {(() => {
                            // Seat changes are an ORGANIZATION billing action now,
                            // managed in the Admin panel (/admin) — hide these
                            // controls for any org user. (Free/legacy non-org pods
                            // keep the inline controls.)
                            if (podRole !== 'admin' || normalizePlanKey(pod?.subscription?.plan) !== 'pro' || orgAccess.orgId) return null;
                            const seatCount = Number(currentSeats || pod?.seatLimit || 1);
                            const memberCount = podMembers.length || 1;
                            const pendingInviteCount = Array.isArray(pod?.pendingInvites) ? pod.pendingInvites.length : 0;
                            const canRemove = seatCount > Math.max(MIN_SEATS || 1, memberCount + pendingInviteCount);
                            const canAdd = seatCount < (MAX_SEATS || 5);
                            return (
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                {canRemove && (
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => requestSeatChange(-1)}
                                    disabled={loadingSeatChangePreview || confirmingSeatChange}
                                    sx={{ textTransform: 'none', borderRadius: '8px', minWidth: 0, px: 1.5 }}
                                  >
                                    − Seat
                                  </Button>
                                )}
                                {canAdd ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => requestSeatChange(1)}
                                    disabled={loadingSeatChangePreview || confirmingSeatChange}
                                    sx={{ textTransform: 'none', borderRadius: '8px', px: 2 }}
                                  >
                                    + Seat ($800/mo)
                                  </Button>
                                ) : (
                                  <Button
                                    size="small"
                                    variant="text"
                                    href="mailto:hi@re-stack.com?subject=Restack%20Practice%20Plan%20Inquiry"
                                    sx={{ textTransform: 'none', borderRadius: '8px', px: 2 }}
                                  >
                                    Need more than {MAX_SEATS}? Contact us
                                  </Button>
                                )}
                              </Box>
                            );
                          })()}
                          {isLoadingMembers && <CircularProgress size={16} />}
                        </Box>

                        {podMembers.map((member) => (
                          <Box
                            key={member.userId}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              py: 1,
                              borderBottom: `1px solid ${alpha(muiTheme.palette.text.secondary, 0.08)}`,
                              gap: 2,
                              "&:last-of-type": { borderBottom: "none" },
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              <Avatar src={member.photoURL || undefined} sx={{ width: 32, height: 32 }}>
                                {(member.name || member.email || "M")[0]}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {member.name || member.email}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                  {member.email || "\u2014"}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={member.role === "admin" ? "Admin" : "Member"}
                                sx={member.role === "admin" ? tagChipSx(muiTheme) : mutedChipSx(muiTheme)}
                              />
                              {podRole === "admin" && member.role !== "admin" && (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => handleRemoveMember(member.userId)}
                                  sx={{ textTransform: "none" }}
                                >
                                  Remove
                                </Button>
                              )}
                              {podRole !== "admin" && member.userId === currentUser?.uid && (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => handleRemoveMember(member.userId)}
                                  sx={{ textTransform: "none" }}
                                >
                                  Leave
                                </Button>
                              )}
                            </Box>
                          </Box>
                        ))}

                        {/* Pending invites — shown beneath joined members.
                            Each row is an email + "Pending" chip + Cancel
                            (admin only). When the invitee accepts, their
                            row moves up into `podMembers` and disappears
                            from here automatically (pod doc onSnapshot). */}
                        {Array.isArray(pod?.pendingInvites) && pod.pendingInvites.length > 0 && (
                          <Box sx={{ mt: podMembers.length > 0 ? 1 : 0 }}>
                            {pod.pendingInvites.map((invite) => {
                              const email = invite.email || '';
                              return (
                                <Box
                                  key={`pending-${email}`}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    py: 1,
                                    borderBottom: `1px solid ${alpha(muiTheme.palette.text.secondary, 0.08)}`,
                                    gap: 2,
                                    "&:last-of-type": { borderBottom: "none" },
                                    opacity: 0.85,
                                  }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                                    <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(muiTheme.palette.warning.main, 0.18), color: 'warning.main' }}>
                                      {(email[0] || '?').toUpperCase()}
                                    </Avatar>
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {email}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                        Waiting for them to sign in and accept
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label="Pending"
                                      sx={statusChipSx(muiTheme, 'warning')}
                                    />
                                    {podRole === "admin" && (
                                      <Button
                                        size="small"
                                        variant="text"
                                        disabled={cancellingInviteFor === email}
                                        onClick={() => handleCancelPendingInvite(email)}
                                        sx={{ textTransform: "none" }}
                                      >
                                        {cancellingInviteFor === email ? 'Cancelling…' : 'Cancel'}
                                      </Button>
                                    )}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        )}

                        {/* ── Invite teammates — nested in the Team card, shown
                            only when there's an open seat (i.e. after the admin
                            adds one). Combines the invite code + email invite. ── */}
                        {podRole === "admin" && !orgAccess.orgId && pod?.inviteCode && openSeats > 0 && (
                          <Box sx={{ mt: 2.5, pt: 2.5, borderTop: `1px solid ${alpha(muiTheme.palette.text.secondary, 0.1)}` }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1 }}>
                              <GroupAddIcon sx={{ color: "primary.main", fontSize: 20 }} />
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Invite to fill your open seat{openSeats === 1 ? '' : 's'}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                              You have {openSeats} open seat{openSeats === 1 ? '' : 's'}. Add a teammate's email below, or share the code. They sign in to <Box component="span" sx={{ fontWeight: 600 }}>re-stack.com</Box> with that email and the invite appears on their Settings to accept in one click.
                            </Typography>

                            {/* Visible code block */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: 1.5,
                                p: 1.5,
                                mb: 2.5,
                                borderRadius: "10px",
                                backgroundColor: alpha(muiTheme.palette.text.primary, 0.03),
                                border: `1px dashed ${alpha(muiTheme.palette.text.primary, 0.2)}`,
                              }}
                            >
                              <Box
                                sx={{
                                  fontFamily: '"SF Mono", Menlo, Consolas, monospace',
                                  fontSize: { xs: "1rem", md: "1.1rem" },
                                  fontWeight: 600,
                                  letterSpacing: "0.1em",
                                  color: "primary.main",
                                  px: 1,
                                }}
                              >
                                {pod.inviteCode}
                              </Box>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Tooltip title={inviteCodeCopied ? "Copied!" : "Copy code"}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                                    onClick={handleCopyInviteCodeInline}
                                    sx={{ borderRadius: "8px", textTransform: "none" }}
                                  >
                                    {inviteCodeCopied ? "Copied" : "Copy code"}
                                  </Button>
                                </Tooltip>
                                <Button
                                  variant="text"
                                  size="small"
                                  onClick={handleCopyJoinLink}
                                  sx={{ borderRadius: "8px", textTransform: "none" }}
                                >
                                  Copy link
                                </Button>
                              </Box>
                            </Box>

                            {/* Email invite */}
                            <Typography
                              variant="overline"
                              sx={{ color: "text.secondary", letterSpacing: 1, fontWeight: 600 }}
                            >
                              Or email an invite
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", alignItems: "center", mt: 1 }}>
                              <TextField
                                size="small"
                                type="email"
                                placeholder="teammate@firm.com"
                                value={inviteEmail}
                                onChange={(event) => setInviteEmail(event.target.value)}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    event.preventDefault();
                                    handleSendInvite();
                                  }
                                }}
                                sx={{ flex: "1 1 240px", maxWidth: 320 }}
                              />
                              <Button
                                variant="contained"
                                onClick={handleSendInvite}
                                disabled={!inviteEmail.trim()}
                                sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}
                              >
                                Send invite
                              </Button>
                            </Box>
                            <Typography variant="caption" sx={{ color: "text.secondary", mt: 1.25, display: "block" }}>
                              We email them a join link and the code, and the invite also shows up on their Settings automatically once they sign in with this email — any provider works.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                    )}

                  </>
                )}
              </>
            )}

            {/* Account section: profile identity plus the destructive zone,
                reference treatment (one card, internal divider, a red button
                sized to content; not a tinted clickable card). */}
            {activeSection === 'account' && (
            <Box
              className="settings-section"
              sx={{ mb: 6 }}
            >
              <Box className="settings-surface-card" sx={{ p: { xs: 2.5, md: 3 } }}>
                {/* Profile identity */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar src={currentUser?.photoURL || undefined} sx={{ width: 44, height: 44, fontSize: 18 }}>
                    {(currentUser?.displayName || currentUser?.email || "?").charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                      {currentUser?.displayName || currentUser?.email}
                    </Typography>
                    <Typography variant="body2" noWrap sx={{ color: "text.secondary" }}>
                      {currentUser?.email}
                    </Typography>
                  </Box>
                  {identity?.roleLabel && (
                    <Chip size="small" variant="outlined" label={identity.roleLabel} sx={tagChipSx(muiTheme)} />
                  )}
                </Box>

                <Divider sx={{ my: 2.5 }} />

                {/* Organization deletion. Org Admin only, and shown above
                    account deletion on purpose: a sole admin has to close the
                    organization before the account can go, so the steps read in
                    the order they must be performed. */}
                {orgAccess.isOrgAdmin && orgAccess.orgId && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Delete organization
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                      Closes {identity?.orgLabel || "this organization"} and releases its seats,
                      credit pool, and firm style guide. Your projects are kept and unlinked.
                      Billing and audit records are retained. This cannot be undone.
                    </Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => {
                        setDeleteOrgError(null);
                        setConfirmDeleteOrgText("");
                        setDeleteOrgDialogOpen(true);
                      }}
                      sx={{ textTransform: "none", mb: 2.5 }}
                    >
                      Delete organization
                    </Button>
                    <Divider sx={{ mb: 2.5 }} />
                  </>
                )}

                {/* Destructive zone */}
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Delete account
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                  {accountDeletionBlocked
                    ? "Transfer your administrative and billing responsibilities before deleting this account. This protects the organization, active subscription, and other members from being orphaned."
                    : orgAccess.orgId || podRole === "member"
                      ? "Deleting your account removes your membership and personal Restack data. Shared organization records, billing history, audit records, and Office files are retained."
                      : "Permanently deletes your Restack account and personal data. Office files remain in Microsoft Office. This cannot be undone."}
                </Typography>
                {accountDeletionBlocked ? (
                  <Button
                    variant="outlined"
                    size="small"
                    href="mailto:hi@re-stack.com?subject=Transfer%20Restack%20account%20ownership"
                    sx={{ textTransform: "none" }}
                  >
                    Contact support to transfer ownership
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => {
                      setDeleteAccountGuidance(null);
                      setConfirmDeleteText("");
                      setDeleteDialogOpen(true);
                    }}
                    sx={{ textTransform: "none" }}
                  >
                    Delete account
                  </Button>
                )}
              </Box>
            </Box>
            )}
            </Box>
          </Box>
        ) : (
          <Box
            className="auth-prompt"
            sx={{
              textAlign: "center",
              py: 8,
              px: { xs: 2, sm: 4, md: '20%' },
            }}
          >
            <Typography
              variant="h5"
              gutterBottom
              sx={{ color: "text.primary" }}
            >
              Please sign in to access settings
            </Typography>
            <Button
              variant="contained"
              onClick={handleBack}
              sx={{
                borderRadius: "8px",
                px: 3,
                py: 1.2,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Go back
            </Button>
          </Box>
        )}

      </Box>

      {/* Global Dialogs & Overlays - Outside Main Container */}

      {/* Seat-change confirmation dialog. Added seats require an exact Stripe
          charge preview. Removed seats have a known zero-refund policy. */}
      <Dialog
        open={!!seatChangePreview}
        onClose={confirmingSeatChange ? undefined : cancelSeatChange}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            border: `1px solid ${muiTheme.palette.divider}`,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {seatChangePreview && seatChangePreview.delta < 0 ? 'Remove a seat' : 'Add a seat'}
        </DialogTitle>
        <DialogContent>
          {seatChangePreview && (() => {
            const renewalDate = seatChangePreview.cycleEndsAt
              ? new Date(seatChangePreview.cycleEndsAt).toLocaleDateString(undefined, { timeZone: 'UTC' })
              : null;
            const isRemoval = seatChangePreview.delta < 0;
            const hasExactAddPreview = isRemoval || (
              seatChangePreview.proratedAmountDueUSD != null
              && seatChangePreview.exactAmountAvailable !== false
            );
            const chargeToday = isRemoval ? 0 : seatChangePreview.proratedAmountDueUSD;
            const chargeDisplay = chargeToday != null
              ? `$${Math.abs(Number(chargeToday)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : null;
            const monthlyDelta = `$${Math.abs(Number(seatChangePreview.monthlyDeltaUSD || 0))
              .toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
            const recurringDelta = Number(seatChangePreview.monthlyDeltaUSD || 0);
            const currentRecurring = `$${Number(seatChangePreview.currentMonthlyUSD || 0).toLocaleString()}`;
            const nextRecurring = `$${Number(seatChangePreview.newMonthlyUSD || 0).toLocaleString()}`;
            const recurringSummary = recurringDelta < 0
              ? `Your recurring amount decreases by ${monthlyDelta}/mo, from ${currentRecurring}/mo to ${nextRecurring}/mo, at renewal.`
              : recurringDelta > 0
                ? `Your recurring amount increases by ${monthlyDelta}/mo, from ${currentRecurring}/mo to ${nextRecurring}/mo, at renewal.`
                : `Your recurring amount remains ${nextRecurring}/mo at renewal.`;

            return (
              <Box>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Your plan goes from <strong>{seatChangePreview.currentSeats}</strong> seat{seatChangePreview.currentSeats === 1 ? '' : 's'} to <strong>{seatChangePreview.newSeats}</strong> seat{seatChangePreview.newSeats === 1 ? '' : 's'}.
                  {isRemoval
                    ? ` ${recurringSummary} Future monthly credit grants become ${Number(seatChangePreview.newMonthlyCredits || 0).toLocaleString()}.`
                    : ` ${recurringSummary} Your monthly credit cap becomes ${Number(seatChangePreview.newMonthlyCredits || 0).toLocaleString()}.`}
                </Typography>
                <Box sx={{
                  p: 2, mb: 2, borderRadius: '10px',
                  border: `1px solid ${muiTheme.palette.divider}`,
                  backgroundColor: alpha(muiTheme.palette.text.primary, 0.025),
                }}>
                  <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.05em' }}>
                    {isRemoval ? 'Charge or refund today' : 'Seat adjustment charged now'}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600, mt: 0.5, mb: 1 }}>
                    {chargeDisplay || 'Exact preview unavailable'}
                    <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary', fontWeight: 500 }}>
                      {(seatChangePreview.currency || 'USD').toUpperCase()}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    {isRemoval
                      ? `The seat count changes immediately. There is no mid-cycle refund. ${recurringSummary}${renewalDate ? ` The renewal date is ${renewalDate}.` : ''} Future monthly credit grants use the new seat count.`
                      : hasExactAddPreview
                        ? `Stripe invoices the prorated seat adjustment now. The amount above excludes unrelated renewal and pending invoice items. After payment succeeds, your rate becomes $${Number(seatChangePreview.newMonthlyUSD || 0).toLocaleString()}/mo and the prorated Restack credits are added to your pool.`
                        : 'Restack cannot add a seat until Stripe provides an exact charge preview.'}
                  </Typography>
                </Box>
                {!hasExactAddPreview && (
                  <Alert severity="warning">
                    No seat change will be made. Close this dialog and try again.
                  </Alert>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={cancelSeatChange}
            disabled={confirmingSeatChange}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={performSeatChange}
            disabled={
              confirmingSeatChange
              || (
                seatChangePreview?.delta > 0
                && (
                  seatChangePreview?.proratedAmountDueUSD == null
                  || seatChangePreview?.exactAmountAvailable === false
                )
              )
            }
            sx={{ textTransform: 'none', borderRadius: '8px', px: 3 }}
          >
            {confirmingSeatChange
              ? 'Processing…'
              : (seatChangePreview && seatChangePreview.delta < 0 ? 'Remove seat' : 'Confirm and pay')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading overlay while fetching the Stripe preview. Brief — usually
          ~1 second. Prevents users from clicking the button twice while
          we're calling Stripe. */}
      {loadingSeatChangePreview && (
        <Box sx={{
          position: 'fixed', inset: 0, zIndex: 1400,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: alpha(muiTheme.palette.common.black, 0.4),
          backdropFilter: 'blur(2px)',
        }}>
          <Box sx={{ backgroundColor: muiTheme.palette.background.paper, borderRadius: '12px', p: 3, display: 'flex', alignItems: 'center', gap: 2, boxShadow: 8 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Calculating exact amount…</Typography>
          </Box>
        </Box>
      )}

      {/* Delete Organization, step 1: what happens. No text entry here, so the
          consequences are read before anything can be typed. */}
      <Dialog
        open={deleteOrgDialogOpen}
        onClose={() => setDeleteOrgDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            border: `1px solid ${muiTheme.palette.divider}`,
          },
        }}
      >
        <DialogTitle>Delete organization</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            Deleting <strong>{identity?.orgLabel || "this organization"}</strong> will:
            <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
              <li>Release its seats, credit pool, and firm style guide</li>
              <li>Remove every member&rsquo;s organization access</li>
              <li>Unlink its projects, which are kept and remain yours</li>
            </ul>
            Billing history and audit records are retained for legal
            recordkeeping. This cannot be undone.
            <br /><br />
            If the organization still has other members or a live paid
            subscription, remove or cancel those first.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOrgDialogOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disableElevation
            onClick={() => {
              setDeleteOrgError(null);
              setConfirmDeleteOrgText("");
              setDeleteOrgConfirmOpen(true);
            }}
            sx={{ textTransform: "none" }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Organization, step 2: type the exact name. The server checks
          the same value, so this is a real gate rather than UI theatre. */}
      <Dialog
        open={deleteOrgConfirmOpen}
        onClose={() => !isDeletingOrg && setDeleteOrgConfirmOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            border: `1px solid ${muiTheme.palette.divider}`,
          },
        }}
      >
        <DialogTitle>Confirm deletion</DialogTitle>
        <DialogContent>
          {deleteOrgError && (
            <Alert severity="error" sx={{ mb: 2 }}>{deleteOrgError}</Alert>
          )}
          <DialogContentText sx={{ mb: 2 }}>
            Type <strong>{identity?.orgLabel || ""}</strong> to confirm. This
            permanently deletes the organization.
          </DialogContentText>
          <TextField
            fullWidth
            size="small"
            autoFocus
            value={confirmDeleteOrgText}
            onChange={(e) => setConfirmDeleteOrgText(e.target.value)}
            placeholder={identity?.orgLabel || "Organization name"}
            disabled={isDeletingOrg}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteOrgConfirmOpen(false)}
            disabled={isDeletingOrg}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disableElevation
            onClick={handleDeleteOrganization}
            disabled={
              isDeletingOrg
              || confirmDeleteOrgText.trim() !== String(identity?.orgLabel || "").trim()
              || !String(identity?.orgLabel || "").trim()
            }
            sx={{ textTransform: "none" }}
          >
            {isDeletingOrg ? "Deleting..." : "Delete organization"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            border: `1px solid ${muiTheme.palette.divider}`,
          },
        }}
      >
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
          >
            <WarningIcon sx={{ color: "warning.main", fontSize: 20 }} />
            This action cannot be undone. Restack will delete or disconnect the following:
          </DialogContentText>
          <DialogContentText component="div" sx={{ mb: 2 }}>
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
              <li>Your user account and profile</li>
              <li>Your personal settings, uploads, and chat history</li>
              <li>Your organization and project memberships</li>
            </ul>
          </DialogContentText>
          <Alert severity="info" sx={{ mb: 2 }}>
            Office files remain in Microsoft Office. Shared organization data, billing history, and audit records are retained for other members and legal recordkeeping.
          </Alert>
          {deleteAccountGuidance && (
            <Alert severity={deleteAccountGuidance.severity || "warning"} sx={{ mb: 2 }}>
              <strong>{deleteAccountGuidance.title}</strong>
              <Box component="span" sx={{ display: "block", mt: 0.5 }}>
                {deleteAccountGuidance.message}
              </Box>
            </Alert>
          )}
          <DialogContentText sx={{ mb: 2 }}>
            To confirm, please type "delete my account" below:
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            value={confirmDeleteText}
            onChange={(e) => setConfirmDeleteText(e.target.value)}
            placeholder="delete my account"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setConfirmDeleteText("");
              setDeleteAccountGuidance(null);
            }}
            disabled={isDeletingAccount}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAccount}
            disabled={
              confirmDeleteText !== "delete my account" || isDeletingAccount
            }
            startIcon={isDeletingAccount && <CircularProgress size={20} />}
            variant="contained"
            color="error"
            disableElevation
          >
            {isDeletingAccount ? "Deleting..." : "Delete account"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Stripe Redirect Overlay */}
      {stripeRedirect && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: alpha(muiTheme.palette.common.black, 0.7),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              backgroundColor: muiTheme.palette.background.paper,
              padding: "32px",
              borderRadius: "12px",
              textAlign: "center",
              maxWidth: "400px",
            }}
          >
            <CircularProgress size={48} sx={{ mb: 3 }} />
            <Typography variant="h6" sx={{ mb: 1, color: "text.primary" }}>
              Redirecting to Stripe
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              You'll be redirected to our secure payment processor. This may
              take a few seconds...
            </Typography>
          </Box>
        </Box>
      )}

    </>
  );
};

export default SettingsScreen;
